import { History } from 'history';
import { camelCase } from 'lodash';
import { config } from '../config';
import { iCrate } from '../types/ICrate';
import RequestService from './Request.service';
import { UpdateCrateDtoToJSON } from '../utils/generateDtos';

interface ApiResponse {
  count: number;
  cursor?: number; // the cursor can be optional if not always present
  remaining: number;
  results: any[]; // if you can type the results further, replace `any[]` with the more specific type
}

export class CrateService {
  static CRATE_STANDARD = 'standard';
  static CRATE_HACKATHON = 'hackathon';
  static CRATE_MARKETPLACE = 'marketplace';
  static MY_CRATES = 'user';

  private static crateEndpoints: { [key: string]: string } = {
    [CrateService.CRATE_STANDARD]: 'crates/standard',
    [CrateService.CRATE_HACKATHON]: 'crates/hackathon',
    [CrateService.CRATE_MARKETPLACE]: 'crates/marketplace',
    [CrateService.MY_CRATES]: 'crates/my-crates',
  };

  // Optional: simplistic cache clearing knob
  private static cacheBuster = 0;
  public static clearCache() {
    CrateService.cacheBuster = Date.now();
  }

  // Mapping for camelCase and original crate keys
  private static readonly iCrateKeys = [
    '_id',
    'Playlist ID',
    'Title',
    'Description',
    'Image',
    'Created Date',
    'Modified Date',
    'Published',
    'withTracks',
    'Tracks',
    'condensed',
    'Hacker',
    'Category',
    'StreamDeezer',
    'StreamAppleMusic',
    'StreamBeatsource',
    'Rekordcloud',
    'StreamSoundcloud',
    'StreamSpotify',
    'StreamTidal',
    'trackCount',
    'createdFrom',
    'Checked',
    'Facebook Live',
    'VDJ',
    'Bookmarks',
    'Status',
    'Type',
    'Live',
    'CloudCrateSpotify',
    'CloudCrateApple',
    'CloudCrateTidal',
    'videoEmbedCode',
  ] as const;

  private static readonly iCrateMapping = CrateService.createMapping(
    CrateService.iCrateKeys,
  );

  // Helper to map from camelCase to original case
  public static mapToOriginalCase(
    currentCrate: Record<string, any>,
  ): Record<string, any> {
    const updatedCrate: Record<string, any> = {};

    for (const key in currentCrate) {
      const originalKey = CrateService.iCrateMapping[key] || key;
      updatedCrate[originalKey] = currentCrate[key];
    }

    return updatedCrate;
  }

  public static async fetchCrate(
    id: string,
    callback?: (data: iCrate) => void,
  ): Promise<iCrate> {
    const url = `${config.API_BASE_URL}/crates/${id}`;
    const data = (await RequestService.fetch({ url, noCache: true })) as iCrate;
    if (callback) callback(data);
    return data;
  }

  public static async fetchCratesByType(
    crateType: string,
    callback: (data: any) => void,
    limit?: number,
    history?: History,
  ) {
    if (CrateService.crateEndpoints[crateType]) {
      const data = await CrateService.fetchCratesFromEndpoint(
        CrateService.crateEndpoints[crateType],
        limit,
        history,
      );
      return callback(data);
    }
    throw new Error(`Invalid crate type: ${crateType}`);
  }

  public static async fetchSelectedCrates(
    crateTypes: string[],
    callback: (data: any[]) => void,
    limit?: number,
    history?: History,
  ) {
    const fetchPromises = crateTypes.map(async (crateType) => {
      try {
        return await CrateService.fetchCratesByType(
          crateType,
          (d) => d,
          limit,
          history,
        );
      } catch (error) {
        console.error(`Error fetching crates for type ${crateType}:`, error);
        return [];
      }
    });

    try {
      const results = await Promise.all(fetchPromises);
      const combinedResults = results.flat().filter(Boolean);
      callback(combinedResults);
    } catch (error) {
      console.error('Error in fetchSelectedCrates:', error);
      callback([]);
    }
  }

  public static async createMyCrate(crateData: iCrate) {
    const { _id, ...dataWithoutId } = crateData as any;
    const url = `${config.API_BASE_URL}/crates`;
    const method = 'POST';
    const body = dataWithoutId;
    return await RequestService.fetch({ url, method, body, noCache: true });
  }

  // Modified `updateMyCrate` method
  public static async updateMyCrate(
    crate: iCrate,
  ): Promise<iCrate | undefined> {
    try {
      const mapToLowerCamelCase = (currentCrate: any) => {
        const updatedCrate: any = {};
        for (const key in currentCrate) {
          const camelCaseKey = camelCase(key);
          updatedCrate[camelCaseKey] = currentCrate[key];
        }
        return updatedCrate;
      };

      const updatedCrate = mapToLowerCamelCase({ ...crate });

      if (updatedCrate.published) {
        updatedCrate.published = new Date(updatedCrate.published);
      }

      const url = `${config.API_BASE_URL}/crates/${crate._id}`;
      const method = 'PATCH';
      const body = UpdateCrateDtoToJSON(updatedCrate);
      const apiResponse = await RequestService.fetch({
        url,
        method,
        body,
        noCache: true,
      });

      const originalKeysCrate = CrateService.mapToOriginalCase(
        apiResponse as unknown as Record<string, any>,
      ) as unknown as iCrate;

      return originalKeysCrate;
    } catch (error) {
      console.error('Error updating crate:', error);
      throw error;
    }
  }

  // TODO: Change to followed crates
  public static fetchFollowedCratePosts(
    callback: (data: any) => void,
    id?: string,
  ) {
    this.fetchSelectedCrates(
      [CrateService.CRATE_STANDARD, CrateService.CRATE_HACKATHON],
      (data) => {
        const cratePostsWithType = data.map((crate) => ({
          ...crate,
          postType: 'crate',
        }));
        callback(cratePostsWithType);
      },
      50,
    );
  }

  private static createMapping(
    keys: readonly string[],
  ): Record<string, string> {
    const mapping: Record<string, string> = {};

    keys.forEach((key) => {
      const camelKey = camelCase(key);
      mapping[camelKey] = key;
    });

    return mapping;
  }

  private static async fetchCratesFromEndpoint(
    endpoint: string,
    limit?: number,
    history?: History,
  ): Promise<any[]> {
    let results: any[] = [];
    let cursor: number | undefined = 0;
    let counter = 0;

    while (cursor !== undefined) {
      const url = `${config.API_BASE_URL}/${endpoint}`;
      const method = 'GET';

      const resp: ApiResponse = (await RequestService.fetch({
        url,
        method,
        cursor,
        limit,
        noCache: cursor === 0,
      })) as ApiResponse;

      if (!resp) break;

      if (resp.results) {
        results = results.concat(resp.results);
        counter += resp.results.length;
      }

      if (resp.cursor) {
        cursor = cursor + resp.count;
      } else {
        cursor = undefined;
      }

      if (limit && counter >= limit) {
        results = results.slice(0, limit);
        break;
      }

      if (!resp.remaining || resp.remaining === 0) {
        break;
      }
    }

    return results;
  }

  private static async fetchStandardCrates(): Promise<any[]> {
    return CrateService.fetchCratesFromEndpoint(
      CrateService.crateEndpoints[CrateService.CRATE_STANDARD],
    );
  }

  private static async fetchHackathonCrates(): Promise<any[]> {
    return CrateService.fetchCratesFromEndpoint(
      CrateService.crateEndpoints[CrateService.CRATE_HACKATHON],
    );
  }

  private static async fetchMarketplaceCrates(): Promise<any[]> {
    return CrateService.fetchCratesFromEndpoint(
      CrateService.crateEndpoints[CrateService.CRATE_MARKETPLACE],
    );
  }
}
